import { initializeApp, getApps, getApp } from "firebase/app";
import { getAnalytics, logEvent, isSupported } from "firebase/analytics";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let analytics = null;
let authReady = null; // Promise that resolves once we have an authenticated Firebase user

function ensureApp() {
  if (!config.apiKey || !config.appId) return null;
  return getApps().length ? getApp() : initializeApp(config);
}

function ensureAuth() {
  if (authReady) return authReady;
  const app = ensureApp();
  if (!app) return Promise.reject(new Error("Firebase not configured"));
  const auth = getAuth(app);
  authReady = new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (user) return resolve(user);
      signInAnonymously(auth).catch(reject);
    });
  });
  return authReady;
}

export function initFirebase() {
  const app = ensureApp();
  if (!app) return;
  isSupported().then((ok) => { if (ok) analytics = getAnalytics(app); }).catch(() => {});
  // Warm up anonymous auth so uploads have a valid token when needed
  ensureAuth().catch((e) => console.warn("[Firebase] anonymous auth failed", e));
}

export function trackEvent(name, params = {}) {
  try { if (analytics) logEvent(analytics, name, params); } catch {}
}

export function isFirebaseReady() {
  return !!(config.apiKey && config.appId && config.storageBucket);
}

/**
 * Resumable upload to Firebase Storage. Waits for anonymous-auth before
 * starting so `request.auth != null` in the storage rules is satisfied.
 */
export function uploadFileResumable(file, folder = "uploads", onProgress = () => {}) {
  return new Promise(async (resolve, reject) => {
    const app = ensureApp();
    if (!app) return reject(new Error("Firebase not configured"));
    try { await ensureAuth(); } catch (e) { return reject(e); }

    const storage = getStorage(app);
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `${folder}/${Date.now()}_${safeName}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file, { contentType: file.type || undefined });
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        onProgress(pct);
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path, size: file.size, contentType: file.type });
        } catch (e) { reject(e); }
      }
    );
  });
}
